import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  MessageCircle, Instagram, Send, Package, AlertCircle,
  ShoppingCart, Plus, Minus, Trash2, X, Facebook, Youtube, Ticket, Check
} from "lucide-react";

// Ícone do TikTok
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Ícone do Mercado Livre (usando imagem)
const MercadoLivreIcon = ({ className }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_8900ab4b-3793-4410-9d57-46d93117eaf5/artifacts/0omkgoji_unnamed.png" 
    alt="Mercado Livre" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Ícone do Shopee
const ShopeeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C9.243 2 7 4.243 7 7h2c0-1.654 1.346-3 3-3s3 1.346 3 3h2c0-2.757-2.243-5-5-5zM5 8v14h14V8H5zm7 10c-2.206 0-4-1.794-4-4h2c0 1.103.897 2 2 2s2-.897 2-2h2c0 2.206-1.794 4-4 4z"/>
  </svg>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

export default function PerfilPublicoPage() {
  const { token } = useParams();
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [carrinho, setCarrinho] = useState([]);
  const [carrinhoAberto, setCarrinhoAberto] = useState(false);
  const [imagemAmpliada, setImagemAmpliada] = useState(null);
  
  // Cupom state
  const [codigoCupom, setCodigoCupom] = useState("");
  const [cupomAplicado, setCupomAplicado] = useState(null);
  const [validandoCupom, setValidandoCupom] = useState(false);

  useEffect(() => {
    const fetchPerfil = async () => {
      try {
        const res = await axios.get(`${API}/perfil/publico/${token}`);
        setPerfil(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Perfil não encontrado");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPerfil();
    } else {
      setError("Link inválido");
      setLoading(false);
    }
  }, [token]);

  // Funções do carrinho
  const adicionarAoCarrinho = (produto) => {
    setCarrinho(prev => {
      const existente = prev.find(item => item.nome === produto.nome);
      if (existente) {
        return prev.map(item => 
          item.nome === produto.nome 
            ? { ...item, quantidade: item.quantidade + 1 }
            : item
        );
      }
      return [...prev, { ...produto, quantidade: 1 }];
    });
  };

  const removerDoCarrinho = (nome) => {
    setCarrinho(prev => prev.filter(item => item.nome !== nome));
  };

  const alterarQuantidade = (nome, delta) => {
    setCarrinho(prev => prev.map(item => {
      if (item.nome === nome) {
        const novaQuantidade = item.quantidade + delta;
        if (novaQuantidade <= 0) return null;
        return { ...item, quantidade: novaQuantidade };
      }
      return item;
    }).filter(Boolean));
  };

  const totalCarrinho = carrinho.reduce((sum, item) => sum + (item.preco * item.quantidade), 0);
  const totalItens = carrinho.reduce((sum, item) => sum + item.quantidade, 0);

  // Cupom functions
  const validarCupom = async () => {
    if (!codigoCupom.trim()) {
      toast.error("Digite o código do cupom");
      return;
    }
    setValidandoCupom(true);
    try {
      const res = await axios.post(`${API}/cupons/validar/${token}?codigo=${codigoCupom}`);
      setCupomAplicado(res.data);
      toast.success(`Cupom "${res.data.codigo}" aplicado!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cupom inválido");
      setCupomAplicado(null);
    } finally {
      setValidandoCupom(false);
    }
  };

  const removerCupom = () => {
    setCupomAplicado(null);
    setCodigoCupom("");
    toast.success("Cupom removido");
  };

  // Cálculo de desconto
  const calcularDesconto = () => {
    if (!cupomAplicado) return 0;
    let desconto = 0;
    if (cupomAplicado.desconto_percentual > 0) {
      desconto += totalCarrinho * (cupomAplicado.desconto_percentual / 100);
    }
    if (cupomAplicado.desconto_valor > 0) {
      desconto += cupomAplicado.desconto_valor;
    }
    return Math.min(desconto, totalCarrinho); // Não pode ser maior que o total
  };

  const valorDesconto = calcularDesconto();
  const totalComDesconto = totalCarrinho - valorDesconto;

  const finalizarPedido = (tipo) => {
    if (carrinho.length === 0) return;

    // Monta a mensagem do pedido
    let mensagem = `🛒 *NOVO PEDIDO*\n\n`;
    mensagem += `📦 *Itens do Pedido:*\n`;
    mensagem += `─────────────────\n`;
    
    carrinho.forEach(item => {
      mensagem += `• ${item.nome}\n`;
      mensagem += `  Qtd: ${item.quantidade} x ${formatCurrency(item.preco)}\n`;
      mensagem += `  Subtotal: ${formatCurrency(item.preco * item.quantidade)}\n\n`;
    });
    
    mensagem += `─────────────────\n`;
    mensagem += `💰 *Subtotal: ${formatCurrency(totalCarrinho)}*\n`;
    
    if (cupomAplicado && valorDesconto > 0) {
      mensagem += `🎟️ *Cupom: ${cupomAplicado.codigo}*\n`;
      mensagem += `💸 *Desconto: -${formatCurrency(valorDesconto)}*\n`;
      mensagem += `✨ *TOTAL FINAL: ${formatCurrency(totalComDesconto)}*\n\n`;
    } else {
      mensagem += `\n`;
    }
    
    mensagem += `Olá! Gostaria de fazer este pedido.`;

    const mensagemCodificada = encodeURIComponent(mensagem);

    if (tipo === 'whatsapp' && perfil.whatsapp) {
      const numero = perfil.whatsapp.replace(/\D/g, '');
      window.open(`https://wa.me/${numero}?text=${mensagemCodificada}`, '_blank');
    } else if (tipo === 'telegram' && perfil.telegram) {
      const usuario = perfil.telegram.replace('@', '');
      window.open(`https://t.me/${usuario}?text=${mensagemCodificada}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-red-500/50" data-testid="perfil-error-state">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Erro</h2>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const whatsappLink = perfil.whatsapp 
    ? `https://wa.me/${perfil.whatsapp.replace(/\D/g, '')}`
    : null;
  const instagramLink = perfil.instagram 
    ? `https://instagram.com/${perfil.instagram.replace('@', '')}`
    : null;
  const telegramLink = perfil.telegram 
    ? `https://t.me/${perfil.telegram.replace('@', '')}`
    : null;
  const facebookLink = perfil.facebook 
    ? `https://facebook.com/${perfil.facebook.replace('@', '')}`
    : null;
  const tiktokLink = perfil.tiktok 
    ? `https://tiktok.com/@${perfil.tiktok.replace('@', '')}`
    : null;
  const youtubeLink = perfil.youtube 
    ? `https://youtube.com/${perfil.youtube.startsWith('@') ? perfil.youtube : '@' + perfil.youtube}`
    : null;
  const mercadopagoLink = perfil.mercadopago || null;
  const shopeeLink = perfil.shopee || null;

  const temAlgumLink = !!(whatsappLink || instagramLink || telegramLink || facebookLink || tiktokLink || youtubeLink || mercadopagoLink || shopeeLink);
  const temAlgumProduto = perfil.produtos && perfil.produtos.length > 0;

  const backgroundStyle = perfil.imagem_fundo_url 
    ? { 
        backgroundImage: `url(${perfil.imagem_fundo_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : {};

  return (
    <div 
      className="min-h-screen relative"
      style={backgroundStyle}
    >
      {/* Overlay escuro para melhor legibilidade */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal de Imagem Ampliada */}
      {imagemAmpliada && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setImagemAmpliada(null)}
        >
          <div className="absolute inset-0 bg-black/90" />
          <div className="relative max-w-lg w-full">
            <button 
              onClick={() => setImagemAmpliada(null)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={imagemAmpliada.imagem} 
                alt={imagemAmpliada.nome}
                className="w-full aspect-square object-cover"
              />
              <div className="p-4">
                <h3 className="text-xl font-bold text-white">{imagemAmpliada.nome}</h3>
                {imagemAmpliada.descricao && (
                  <p className="text-white/70 mt-1">{imagemAmpliada.descricao}</p>
                )}
                {imagemAmpliada.preco > 0 && (
                  <p className="text-2xl font-bold text-emerald-400 mt-2">
                    {formatCurrency(imagemAmpliada.preco)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botão do Carrinho Flutuante */}
      {totalItens > 0 && (
        <button
          onClick={() => setCarrinhoAberto(true)}
          className="fixed bottom-6 right-6 z-50 bg-emerald-500 hover:bg-emerald-600 text-white p-4 rounded-full shadow-lg shadow-emerald-500/30 transition-all hover:scale-110"
        >
          <ShoppingCart className="w-6 h-6" />
          <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2">
            {totalItens}
          </Badge>
        </button>
      )}

      {/* Modal do Carrinho */}
      {carrinhoAberto && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/70"
            onClick={() => setCarrinhoAberto(false)}
          />
          <div className="relative bg-slate-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-hidden">
            {/* Header do Carrinho */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Meu Carrinho ({totalItens})
              </h3>
              <button 
                onClick={() => setCarrinhoAberto(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Itens do Carrinho */}
            <div className="p-4 overflow-y-auto max-h-[40vh]">
              {carrinho.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Carrinho vazio</p>
              ) : (
                <div className="space-y-3">
                  {carrinho.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 bg-slate-800 p-3 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-white">{item.nome}</p>
                        <p className="text-emerald-400 text-sm">{formatCurrency(item.preco)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => alterarQuantidade(item.nome, -1)}
                          className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-white w-8 text-center">{item.quantidade}</span>
                        <button 
                          onClick={() => alterarQuantidade(item.nome, 1)}
                          className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => removerDoCarrinho(item.nome)}
                          className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center text-red-500 hover:bg-red-500/30 ml-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer do Carrinho */}
            {carrinho.length > 0 && (
              <div className="p-4 border-t border-slate-700 space-y-4">
                {/* Campo de Cupom */}
                <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <p className="text-xs text-purple-400 mb-2 flex items-center gap-1">
                    <Ticket className="w-3 h-3" />
                    Cupom de desconto
                  </p>
                  {cupomAplicado ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="font-mono font-bold text-purple-300">{cupomAplicado.codigo}</span>
                        <span className="text-green-400 text-sm">
                          {cupomAplicado.desconto_percentual > 0 && `-${cupomAplicado.desconto_percentual}%`}
                          {cupomAplicado.desconto_valor > 0 && ` -${formatCurrency(cupomAplicado.desconto_valor)}`}
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={removerCupom} className="text-red-400 hover:text-red-300 h-7 px-2">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Digite o código"
                        value={codigoCupom}
                        onChange={(e) => setCodigoCupom(e.target.value.toUpperCase())}
                        className="bg-slate-800 border-slate-600 font-mono uppercase h-9"
                      />
                      <Button 
                        onClick={validarCupom} 
                        disabled={validandoCupom}
                        className="bg-purple-600 hover:bg-purple-700 h-9 px-3"
                      >
                        {validandoCupom ? "..." : "Aplicar"}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Resumo de Valores */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(totalCarrinho)}</span>
                  </div>
                  {cupomAplicado && valorDesconto > 0 && (
                    <div className="flex items-center justify-between text-green-400">
                      <span>Desconto ({cupomAplicado.codigo}):</span>
                      <span>-{formatCurrency(valorDesconto)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-600">
                    <span className="text-white font-medium">Total:</span>
                    <span className="text-2xl font-bold text-emerald-400">{formatCurrency(totalComDesconto)}</span>
                  </div>
                </div>
                
                <p className="text-center text-slate-400 text-sm">Enviar pedido via:</p>
                
                <div className="flex gap-3">
                  {perfil.whatsapp && (
                    <Button 
                      onClick={() => finalizarPedido('whatsapp')}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  )}
                  {perfil.telegram && (
                    <Button 
                      onClick={() => finalizarPedido('telegram')}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Telegram
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="relative z-10 min-h-screen p-4 md:p-8">
        <div className="max-w-2xl mx-auto space-y-8">
          
          {/* Header com Logo e Nome */}
          <div className="text-center pt-8">
            {perfil.logo_url && (
              <div className="mb-6">
                <img 
                  src={perfil.logo_url} 
                  alt={perfil.nome_negocio || "Logo"}
                  className="w-32 h-32 object-contain mx-auto"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
            
            {perfil.nome_negocio && (
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 drop-shadow-lg">
                {perfil.nome_negocio}
              </h1>
            )}
          </div>

          {/* Redes Sociais - Ícones lado a lado */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            {/* WhatsApp */}
            {whatsappLink && (
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center shadow-lg shadow-green-500/30 transition-all group-hover:scale-110">
                  <MessageCircle className="w-7 h-7 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">WhatsApp</span>
              </a>
            )}

            {/* Instagram */}
            {instagramLink && (
              <a 
                href={instagramLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 flex items-center justify-center shadow-lg shadow-pink-500/30 transition-all group-hover:scale-110">
                  <Instagram className="w-7 h-7 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">Instagram</span>
              </a>
            )}

            {/* Telegram */}
            {telegramLink && (
              <a 
                href={telegramLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30 transition-all group-hover:scale-110">
                  <Send className="w-7 h-7 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">Telegram</span>
              </a>
            )}

            {/* Facebook */}
            {facebookLink && (
              <a 
                href={facebookLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center shadow-lg shadow-blue-600/30 transition-all group-hover:scale-110">
                  <Facebook className="w-7 h-7 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">Facebook</span>
              </a>
            )}

            {/* TikTok */}
            {tiktokLink && (
              <a 
                href={tiktokLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-black hover:bg-gray-900 flex items-center justify-center shadow-lg shadow-black/30 transition-all group-hover:scale-110 border border-white/20">
                  <TikTokIcon className="w-7 h-7 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">TikTok</span>
              </a>
            )}

            {/* YouTube */}
            {youtubeLink && (
              <a 
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center shadow-lg shadow-red-600/30 transition-all group-hover:scale-110">
                  <Youtube className="w-7 h-7 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">YouTube</span>
              </a>
            )}

            {/* Mercado Livre */}
            {mercadopagoLink && (
              <a 
                href={mercadopagoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center shadow-lg shadow-yellow-400/30 transition-all group-hover:scale-110 overflow-hidden">
                  <MercadoLivreIcon className="w-10 h-10" />
                </div>
                <span className="text-white/80 text-xs font-medium">M. Livre</span>
              </a>
            )}

            {/* Shopee */}
            {shopeeLink && (
              <a 
                href={shopeeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30 transition-all group-hover:scale-110">
                  <ShopeeIcon className="w-7 h-7 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">Shopee</span>
              </a>
            )}
          </div>

          {/* Produtos em Destaque */}
          {perfil.produtos && perfil.produtos.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-white text-center mb-6 flex items-center justify-center gap-2">
                <Package className="w-6 h-6" />
                Produtos em Destaque
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {perfil.produtos.map((produto, index) => {
                  const noCarrinho = carrinho.find(item => item.nome === produto.nome);
                  return (
                    <Card 
                      key={index} 
                      className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden hover:bg-white/20 transition-all"
                    >
                      {produto.imagem_url && (
                        <div 
                          className="h-40 bg-black/20 cursor-pointer"
                          onClick={() => setImagemAmpliada({
                            imagem: produto.imagem_url,
                            nome: produto.nome,
                            descricao: produto.descricao,
                            preco: produto.preco
                          })}
                        >
                          <img 
                            src={produto.imagem_url} 
                            alt={produto.nome}
                            className="w-full h-full object-cover hover:scale-105 transition-transform"
                            onError={(e) => e.target.parentElement.style.display = 'none'}
                          />
                        </div>
                      )}
                      <CardContent className="p-4">
                        <h3 className="font-bold text-white text-lg">{produto.nome}</h3>
                        {produto.descricao && (
                          <p className="text-white/70 text-sm mt-1 line-clamp-2">
                            {produto.descricao}
                          </p>
                        )}
                        {produto.preco > 0 && (
                          <div className="flex items-center justify-between mt-3">
                            <p className="text-2xl font-bold text-emerald-400">
                              {formatCurrency(produto.preco)}
                            </p>
                            <button
                              onClick={() => adicionarAoCarrinho(produto)}
                              className={`p-3 rounded-full transition-all ${
                                noCarrinho 
                                  ? 'bg-emerald-500 text-white' 
                                  : 'bg-white/20 text-white hover:bg-emerald-500'
                              }`}
                            >
                              <ShoppingCart className="w-5 h-5" />
                              {noCarrinho && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-xs w-5 h-5 rounded-full flex items-center justify-center">
                                  {noCarrinho.quantidade}
                                </span>
                              )}
                            </button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Estado vazio: perfil existe mas sem links/produtos configurados */}
          {!temAlgumLink && !temAlgumProduto && (
            <div className="mt-8">
              <Card className="bg-white/10 backdrop-blur-md border-amber-500/40 max-w-md mx-auto" data-testid="perfil-empty-state">
                <CardContent className="p-6 text-center">
                  <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-1">Perfil em configuração</h3>
                  <p className="text-white/70 text-sm">
                    Este perfil ainda não configurou seus contatos ou produtos.
                    Volte em alguns instantes.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-white/50 text-sm pt-8 pb-4" data-testid="footer-contato">
            <p>{temAlgumLink ? "Entre em contato pelos links acima" : ""}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
